import React, { useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";

import TopNav from "../components/navigation/TopNav";
import StandardScreen, {
  ScreenContent,
} from "../components/layout/StandardScreen";
import Stack from "../components/layout/Stack";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import FormField from "../components/forms/FormField";
import TextInput from "../components/forms/TextInput";

const shellStyle = {
  width: "100%",
  maxWidth: "560px",
  margin: "0 auto",
};

const errorTextStyle = {
  color: "var(--color-danger)",
};

const ChangePassword = () => {
  const { t } = useTranslation();

  const API_URL = useMemo(
    () => (process.env.REACT_APP_API_URL || "").trim(),
    []
  );

  const token = localStorage.getItem("token");

  const headers = useMemo(() => {
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const { user, loadingUser } = useAuth();

  const [error, setError] = useState("");

  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    confirm_new_password: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const allowedStatuses = ["approved", "pending", "suspended"];
  const allowed =
    user?.role === "admin" ||
    allowedStatuses.includes(String(user?.subscription_status || ""));

  const handleChange = (e) => {
    const { name, value } = e.target;
    const digitsOnly = value.replace(/\D/g, "");

    setForm((prev) => ({
      ...prev,
      [name]: digitsOnly,
    }));
  };

  const newPasswordValid = /^\d{4}$/.test(form.new_password);
  const newPasswordsMatch =
    form.new_password === form.confirm_new_password;

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setError("");

    if (!form.current_password || form.current_password.length !== 4) {
      setError(t("change_password.errors.current_invalid"));
      return;
    }

    if (!newPasswordValid) {
      setError(t("change_password.errors.new_invalid"));
      return;
    }

    if (!newPasswordsMatch) {
      setError(t("change_password.errors.mismatch"));
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(
        `${API_URL}/auth/change-password`,
        {
          current_password: form.current_password,
          new_password: form.new_password,
        },
        { headers }
      );

      alert(t("change_password.success"));

      setForm({
        current_password: "",
        new_password: "",
        confirm_new_password: "",
      });
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        t("change_password.errors.failed");
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingUser) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("change_password.title")} mode="main" />}
        isLoading
      />
    );
  }

  if (!user) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("change_password.title")} mode="main" />}
        isError
        errorState={
          <div style={errorTextStyle}>
            {t("change_password.errors.no_session")}
          </div>
        }
      />
    );
  }

  if (!allowed) {
    return (
      <StandardScreen topNav={<TopNav title={t("change_password.title")} mode="main" />}>
        <ScreenContent>
          <div style={shellStyle}>
            <Card variant="accent">
              <strong>{t("change_password.errors.no_access")}</strong>
            </Card>
          </div>
        </ScreenContent>
      </StandardScreen>
    );
  }

  return (
    <StandardScreen topNav={<TopNav title={t("change_password.title")} mode="main" />}>
      <ScreenContent>
        <div style={shellStyle}>
          <Card
            title={t("change_password.title")}
            subtitle={t("change_password.subtitle")}
          >
            <Stack gap="4">
              {error ? <div style={errorTextStyle}>{error}</div> : null}

              <FormField label={t("change_password.fields.current")}>
                <TextInput
                  type="password"
                  name="current_password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder={t("change_password.placeholders.current")}
                  value={form.current_password}
                  onChange={handleChange}
                  maxLength={4}
                  autoComplete="current-password"
                  disabled={isSubmitting}
                />
              </FormField>

              <FormField
                label={t("change_password.fields.new")}
                error={
                  !newPasswordValid && form.new_password.length > 0
                    ? t("change_password.errors.new_invalid")
                    : ""
                }
              >
                <TextInput
                  type="password"
                  name="new_password"
                  placeholder={t("change_password.placeholders.new")}
                  value={form.new_password}
                  onChange={handleChange}
                  maxLength={4}
                  disabled={isSubmitting}
                />
              </FormField>

              <FormField
                label={t("change_password.fields.confirm")}
                error={
                  form.confirm_new_password.length > 0 && !newPasswordsMatch
                    ? t("change_password.errors.mismatch_short")
                    : ""
                }
              >
                <TextInput
                  type="password"
                  name="confirm_new_password"
                  placeholder={t("change_password.placeholders.confirm")}
                  value={form.confirm_new_password}
                  onChange={handleChange}
                  maxLength={4}
                  disabled={isSubmitting}
                />
              </FormField>

              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                size="lg"
                fullWidth
              >
                {isSubmitting
                  ? t("change_password.actions.updating")
                  : t("change_password.actions.update")}
              </Button>
            </Stack>
          </Card>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
};

export default ChangePassword;